import { badgeColors, tagColorClasses } from "#src/commonV2/badges/utils";
import { ButtonV2 } from "#src/commonV2/Button";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { endpointService } from "#src/services/endpoint";
import { PeripheralBlockType } from "#src/services/endpoint/dlp/enums";
import { tagService } from "#src/services/tags";
import { Monitor01, Tag01 } from "@untitled-ui/icons-react";
import { getFixedT } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxLooksLikeRadioV2 } from "#src/commonV2/inputs/CheckboxLooksLikeRadio";
import dlpService from "#src/services/endpoint/dlp";
import { IPeripheralPolicyItem } from "#src/services/endpoint/dlp/types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { createToast } from "#src/common/system/toasts";
import { DeviceTagType } from "#src/services/endpoint/enums";
import {
  Tag,
  TagsInputWithSearch,
  TagsInputWithSearchRef,
} from "#src/commonV2/TagsInputWithSearch";
import { FormFieldWrapper } from "#src/commonV2/form/helpers/FieldWrapper";
import { useFormDropdown } from "#src/commonV2/form/Dropdown/useFormDropdown";
import { FormDropdown } from "#src/commonV2/form/Dropdown/FormDropdown";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

const selectionTFunc = getFixedT(
  null,
  "endpoint",
  "dlp.externalStorage.peripheralPolicy.configModal"
);

const blockTypeItems: IFilterItemV2[] = [
  {
    key: "read&write",
    value: PeripheralBlockType.READ_WRITE,
    getLabel: () => selectionTFunc("policyBlockType.readAndWrite"),
  },
  {
    key: "write",
    value: PeripheralBlockType.WRITE,
    getLabel: () => selectionTFunc("policyBlockType.write"),
  },
  {
    key: "read",
    value: PeripheralBlockType.READ,
    getLabel: () => selectionTFunc("policyBlockType.read"),
  },
];

interface IEndpointDlpPeripheralPolicyConfigModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  configuringPolicy: IPeripheralPolicyItem | null;
}

export const EndpointDlpPeripheralPolicyConfigModal = ({
  workspaceId,
  open,
  onClose,
  onRefresh,
  configuringPolicy,
}: IEndpointDlpPeripheralPolicyConfigModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "dlp.externalStorage.peripheralPolicy.configModal",
  });

  // --------------- INITIALIZATIONS ---------------

  const applyDeviceRef = useRef<TagsInputWithSearchRef>(null);
  const excludeDeviceRef = useRef<TagsInputWithSearchRef>(null);

  const [applyAll, setApplyAll] = useState<boolean>(false);
  const [applyDevice, setApplyDevice] = useState<Tag[]>([]);
  const [excludedDevice, setExcludedDevice] = useState<Tag[]>([]);

  const defaultType = useMemo(() => {
    if (!configuringPolicy) return blockTypeItems[0];

    const getBlockType = blockTypeItems.find(
      (item) => item.value === configuringPolicy.block_type
    );

    if (getBlockType) return getBlockType;

    return blockTypeItems[0];
  }, [configuringPolicy]);

  const {
    selected: blockType,
    reset: resetBlockType,
    renderProps: blockTypeRenderProps,
  } = useFormDropdown({
    id: "block_type",
    label: t("policyBlockType.label"),
    defaultSelected: defaultType,
    options: blockTypeItems,
  });

  // --------------- METHODS ---------------

  const searchTags = async (
    workspaceId: string,
    query: string
  ): Promise<Tag[]> => {
    const [tags, devices] = await Promise.all([
      tagService
        .listTagsPaging(workspaceId, {
          q: query,
          page: 1,
          size: 10,
        })
        .catch(apiErrorHandler),
      endpointService
        .listDevices(workspaceId, {
          q: query,
          page: 1,
          size: 10,
          sort: undefined,
          os: undefined,
          host_status: undefined,
          tag: undefined,
          policy_compliance: undefined,
          policy_compliance_id: undefined,
          policy_compliance_status: undefined,
          program_id: undefined,
          asset_type: undefined,
          backup_enabled: undefined,
        })
        .catch(apiErrorHandler),
    ]);

    // Convert tags to Tag format
    const tagResults: Tag[] = tags.results.map((tag) => {
      const hash = Array.from(tag.name).reduce(
        (prev, cur) => prev + cur.charCodeAt(0),
        0
      );

      const chooseColor = badgeColors[hash % badgeColors.length];

      return {
        id: tag.id.toString(),
        name: tag.name,
        extraData: {
          id: tag.id,
          type: DeviceTagType.TAG,
        },
        badgeOptions: {
          defaultIcon: true,
          noColor: false,
        },
        dropdownOptions: {
          icon: (
            <Tag01
              className={`h-5 w-5 min-w-5 ${tagColorClasses[chooseColor]}`}
            />
          ),
        },
      };
    });

    // Convert devices to Tag format
    const deviceResults: Tag[] = devices.results.map((device) => ({
      id: device.id.toString(),
      name: device.name,
      extraData: {
        id: device.id,
        type: DeviceTagType.HOST,
      },
      badgeOptions: {
        icon: <Monitor01 className="h-4 w-4 min-w-4" />,
        noColor: true,
      },
      dropdownOptions: {
        icon: <Monitor01 className="h-5 w-5 min-w-5" />,
      },
    }));

    // Combine results
    return [...deviceResults, ...tagResults];
  };

  const searchExclude = useCallback(
    async (workspaceId: string, query: string): Promise<Tag[]> => {
      const [tags, devices] = await Promise.all([
        applyAll
          ? tagService
              .listTagsPaging(workspaceId, {
                q: query,
                page: 1,
                size: 10,
              })
              .catch(apiErrorHandler)
          : Promise.resolve({ count: 0, results: [] }),
        endpointService
          .listDevices(workspaceId, {
            q: query,
            page: 1,
            size: 10,
            sort: undefined,
            os: undefined,
            host_status: undefined,
            tag: applyAll
              ? undefined
              : applyDevice.map((item) => item.name).join(","),
            policy_compliance: undefined,
            policy_compliance_id: undefined,
            policy_compliance_status: undefined,
            program_id: undefined,
            asset_type: undefined,
            backup_enabled: undefined,
          })
          .catch(apiErrorHandler),
      ]);

      // Convert tags to Tag format
      const tagResults: Tag[] = tags.results.map((tag) => {
        const hash = Array.from(tag.name).reduce(
          (prev, cur) => prev + cur.charCodeAt(0),
          0
        );

        const chooseColor = badgeColors[hash % badgeColors.length];

        return {
          id: tag.id.toString(),
          name: tag.name,
          extraData: {
            id: tag.id,
            type: DeviceTagType.TAG,
          },
          badgeOptions: {
            defaultIcon: true,
            noColor: false,
          },
          dropdownOptions: {
            icon: (
              <Tag01
                className={`h-5 w-5 min-w-5 ${tagColorClasses[chooseColor]}`}
              />
            ),
          },
        };
      });

      // Convert devices to Tag format
      const deviceResults: Tag[] = devices.results.map((device) => ({
        id: device.id.toString(),
        name: device.name,
        extraData: {
          id: device.id,
          type: DeviceTagType.HOST,
        },
        badgeOptions: {
          icon: <Monitor01 className="h-4 w-4 min-w-4" />,
          noColor: true,
        },
        dropdownOptions: {
          icon: <Monitor01 className="h-5 w-5 min-w-5" />,
        },
      }));

      // Combine results
      return [...deviceResults, ...tagResults];
    },
    [applyAll, applyDevice]
  );

  const onCloseAndCleanup = () => {
    onClose();
    resetBlockType();
    setApplyDevice([]);
  };

  const submitPolicy = () => {
    if (configuringPolicy) {
      if (!applyAll && applyDevice.length === 0) {
        createToast({
          type: "warning",
          message: t("notification.emptySource.message"),
          detail: t("notification.emptySource.detail"),
        });
        return;
      }

      dlpService
        .updatePeripheralPolicies(workspaceId, configuringPolicy.id, {
          block_type: blockType.value ?? "",
          enabled: true,
          sources: applyDevice.map((item) => ({
            id: item.extraData?.id,
            type: item.extraData?.type,
          })),
          exclude: excludedDevice.map((item) => ({
            id: item.extraData?.id,
            type: item.extraData?.type,
          })),
          all_hosts: applyAll,
        })
        .then(() => {
          onRefresh();
          onCloseAndCleanup();
        })
        .catch(apiErrorHandler);
    }
  };

  const onChangeApplyAll = () => {
    if (applyAll) {
      setApplyAll(false);
      setExcludedDevice([]);
      return;
    }
    // TODO: setExcludeDevice filter out
    setApplyDevice([]);
    setApplyAll(true);
  };

  // --------------- COMPUTED ---------------

  // Wrap the search functions to pass workspaceId
  const wrappedSearchApplicableTags = useCallback(
    (query: string) => searchTags(workspaceId, query),
    [workspaceId]
  );

  const wrappedSearchExcludableTags = useCallback(
    (query: string) => searchExclude(workspaceId, query),
    [workspaceId, searchExclude]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (configuringPolicy) {
      setApplyAll(configuringPolicy.all_hosts);
      setApplyDevice(
        configuringPolicy.sources.map((tag) => {
          const hash = Array.from(tag.name).reduce(
            (prev, cur) => prev + cur.charCodeAt(0),
            0
          );

          const chooseColor = badgeColors[hash % badgeColors.length];

          return {
            id: tag.id.toString(),
            name: tag.name,
            extraData: {
              id: tag.id,
              type: tag.type,
            },
            badgeOptions: {
              defaultIcon: true,
              noColor: false,
            },
            dropdownOptions: {
              icon: (
                <Tag01
                  className={`h-5 w-5 min-w-5 ${tagColorClasses[chooseColor]}`}
                />
              ),
            },
          };
        })
      );
      setExcludedDevice(
        configuringPolicy.exclude.map((tag) => ({
          id: tag.id.toString(),
          name: tag.name,
          extraData: {
            id: tag.id,
            type: tag.type,
          },
          badgeOptions: {
            icon: <Monitor01 className="h-4 w-4 min-w-4" />,
            noColor: true,
          },
          dropdownOptions: {
            icon: <Monitor01 className="h-5 w-5 min-w-5" />,
          },
        }))
      );
    }
  }, [configuringPolicy]);

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onCloseAndCleanup} className="w-[47rem]">
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      <div className="flex flex-col gap-6">
        <FormDropdown {...blockTypeRenderProps} />
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col gap-3">
            <span className="flex items-center gap-0.5 text-sm font-medium text-gray-v2-700">
              {t("appliedEndpointDevices.label")}{" "}
              <span className="text-brand-v2-600">*</span>
            </span>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-v2-700">
              <CheckboxLooksLikeRadioV2
                checked={applyAll}
                onClick={onChangeApplyAll}
              />
              {t("appliedEndpointDevices.applyToAll")}
            </label>
            <TagsInputWithSearch
              ref={applyDeviceRef}
              tags={applyDevice}
              setTags={(tags) => {
                setApplyDevice(tags);

                if (
                  tags.length === 0 ||
                  !tags.some((tag) => tag.extraData?.type === DeviceTagType.TAG)
                ) {
                  setExcludedDevice([]);
                }
              }}
              searchFunction={wrappedSearchApplicableTags}
              placeholder={t("appliedEndpointDevices.placeholder")}
              emptyState={
                <div className="text-gray-v2-600 text-center p-2">
                  {"No tags found"}
                </div>
              }
              disabled={applyAll}
            />
          </div>
          <FormFieldWrapper
            label={t("excluded.label")}
            error={null}
            tooltip={t("excluded.tooltip")}
          >
            <TagsInputWithSearch
              ref={excludeDeviceRef}
              tags={excludedDevice}
              setTags={setExcludedDevice}
              searchFunction={wrappedSearchExcludableTags}
              placeholder={t("excluded.placeholder")}
              emptyState={
                <div className="text-gray-v2-600 text-center p-2">
                  {"No tags found"}
                </div>
              }
              disabled={
                !applyAll &&
                (applyDevice.length === 0 ||
                  !applyDevice.some(
                    (tag) => tag.extraData?.type === DeviceTagType.TAG
                  ))
              }
            />
          </FormFieldWrapper>
        </div>
        <div className="flex justify-end gap-2">
          <ButtonV2 variant="secondary" onClick={onCloseAndCleanup}>
            {t("button.cancel")}
          </ButtonV2>
          <ButtonV2 onClick={submitPolicy}>{t("button.save")}</ButtonV2>
        </div>
      </div>
    </ModalV2>
  );
};
